// src/components/ContactDetails.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaSmile,
  FaMeh,
  FaFrown,
  FaSadTear,
  FaArrowLeft,
} from "react-icons/fa";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Tools from "../utils/Tools";

function ContactDetails() {
  const [contact, setContact] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [filters] = useState(
    location.state?.filters || {
      excludeWeekends: true,
      dateRange: { value: "today", label: "Today" },
    }
  );

  const [transcript, setTranscript] = useState(null);

  const handleBackClick = () => {
    navigate(location.state?.referrer || -1);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.append("ContactId", id);
    Tools.sendRequest("get-contact", queryParams.toString()).then((data) => {
      setContact(data);
      if (data.transcript) {
        setTranscript(data.transcript);
      }
    });
  }, [id]);

  if (!contact) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loader"></div>
      </div>
    );
  }

  const formatTranscript = (text) => {
    return text.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line.startsWith("Agent:") ? (
          <>
            <strong>Agent:</strong>
            {line.substring(6)}
          </>
        ) : line.startsWith("Customer:") ? (
          <>
            <strong>Customer:</strong>
            {line.substring(9)}
          </>
        ) : (
          line
        )}
        <br />
      </React.Fragment>
    ));
  };

  const formattedDate = new Date(contact.call_date).toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
  });

  // Function to get a small sentiment icon
  const getSentimentIconSmall = (sentiment, label) => {
    const sentimentValue = parseFloat(sentiment);

    if (isNaN(sentimentValue)) {
      return null;
    }

    let icon;
    let color;

    if (sentimentValue > 0.75) {
      icon = <FaSmile size="1.5em" />;
      color = "text-green-600";
    } else if (sentimentValue > 0.25) {
      icon = <FaSmile size="1.5em" />;
      color = "text-green-400";
    } else if (sentimentValue >= -0.25) {
      icon = <FaMeh size="1.5em" />;
      color = "text-gray-500";
    } else if (sentimentValue >= -0.75) {
      icon = <FaFrown size="1.5em" />;
      color = "text-red-300";
    } else {
      icon = <FaSadTear size="1.5em" />;
      color = "text-red-500";
    }

    return (
      <Tippy content={`${label}: ${sentimentValue.toFixed(2)}`}>
        <span className={`ml-2 ${color}`}>{icon}</span>
      </Tippy>
    );
  };

  // Function to get labels and colors based on score
  const getLabelAndColor = (score, field) => {
    const value = parseFloat(score);

    if (isNaN(value)) {
      return (
        <Tippy content="Score: N/A">
          <span className="text-gray-500 ml-1">N/A</span>
        </Tippy>
      );
    }

    let label = "";
    let color = "";

    if (field === "performance") {
      if (value > 0.8) {
        label = "Excellent";
        color = "text-green-700";
      } else if (value > 0.6) {
        label = "Good";
        color = "text-green-500";
      } else if (value >= 0.4) {
        label = "Average";
        color = "text-yellow-500";
      } else if (value >= 0.2) {
        label = "Needs Improvement";
        color = "text-orange-500";
      } else {
        label = "Unsatisfactory";
        color = "text-red-600";
      }
    } else if (field === "experience") {
      if (value > 0.8) {
        label = "Very Positive";
        color = "text-green-600";
      } else if (value > 0.6) {
        label = "Positive";
        color = "text-green-400";
      } else if (value >= 0.4) {
        label = "Neutral";
        color = "text-yellow-500";
      } else if (value >= 0.2) {
        label = "Negative";
        color = "text-red-400";
      } else {
        label = "Very Negative";
        color = "text-red-600";
      }
    } else if (field === "satisfaction") {
      if (value > 0.8) {
        label = "Very Satisfied";
        color = "text-green-700";
      } else if (value > 0.6) {
        label = "Satisfied";
        color = "text-green-500";
      } else if (value >= 0.4) {
        label = "Neutral";
        color = "text-yellow-500";
      } else if (value >= 0.2) {
        label = "Dissatisfied";
        color = "text-orange-500";
      } else {
        label = "Very Dissatisfied";
        color = "text-red-600";
      }
    } else {
      label = value.toFixed(2);
      color = "text-gray-500";
    }
    return (
      <Tippy content={`Score: ${value.toFixed(2)}`}>
        <span className={`${color} ml-1`}>{label}</span>
      </Tippy>
    );
  };

  console.log("Got contact: ", contact);
  // Parse the percentages and ensure they are numbers
  const agentPercent = parseFloat(contact.agent_percent) || 0;
  const userPercent = parseFloat(contact.user_percent) || 0;
  const silencePercent = parseFloat(contact.silence_percent) || 0;

  return (
    <div className="container mx-auto p-6">
      <button
        className="text-gray-600 hover:text-gray-800 mb-4 flex items-center"
        onClick={handleBackClick}
      >
        <FaArrowLeft className="mr-1" /> Back
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-bold">Contact Details</h1>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Call Information Card */}
        <div className="bg-white shadow rounded-lg p-6 bg-gradient-to-br from-purple-50 to-purple-100">
          <h2 className="text-xl font-semibold mb-4">Call Information</h2>
          <p className="mb-2">
            <strong>Date:</strong> {formattedDate}
          </p>
          <p className="mb-2">
            <strong>Call ID:</strong> {contact.contact_id}
          </p>
          <p className="mb-2">
            <strong>Origination:</strong> {contact.initiation_method}
          </p>
          <p className="mb-2">
            <strong>Queue:</strong> {contact.queue}
          </p>

          <p className="mb-2">
            <strong>Categories:</strong>
            <span className="ml-2">
              {contact.categories.map((category, index) => (
                <span
                  key={index}
                  className="bg-green-100 text-green-800 text-xs font-medium mr-2 mb-2 px-2.5 py-0.5 rounded inline-block"
                >
                  {category.trim()}
                </span>
              ))}
            </span>
          </p>

          <p className="mb-2">
            <strong>Intents:</strong>
            <span className="ml-2">
              {contact.intents.map((intent, index) => (
                <Tippy
                  key={index}
                  content={
                    <div>
                      <div>
                        <span className="text-xs text-gray-400">
                          Category:{" "}
                        </span>
                        {intent.category}
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">
                          Major Intent:{" "}
                        </span>
                        {intent.standard_intent}
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">
                          Minor Intent:{" "}
                        </span>
                        {intent.original_intent}
                      </div>
                    </div>
                  }
                >
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded cursor-pointer">
                    {intent.standard_intent}
                  </span>
                </Tippy>
              ))}
            </span>
          </p>

          <p className="mb-2">
            <strong>Summary:</strong> {contact.summary}
          </p>
        </div>

        {/* Agent Card */}
        <div className="bg-white shadow rounded-lg p-6 bg-gradient-to-br from-blue-50 to-blue-100">
          <h2 className="text-xl font-semibold mb-4">Agent Information</h2>
          <p className="mb-2 flex items-center">
            <strong>Agent Name:</strong>
            <span className="ml-2">{contact.agent}</span>
          </p>
          <p className="mb-2 flex items-center">
            <strong>Sentiment:</strong>{" "}
            {getSentimentIconSmall(contact.agent_sentiment, "Agent Sentiment")}
          </p>
          <p className="mb-1">
            <strong>Performance:</strong>{" "}
            {getLabelAndColor(contact.agent_performance_score, "performance")}
          </p>
          <p className="text-gray-500 text-sm">
            {contact.agent_performance_reason}
          </p>
        </div>

        {/* Customer Card */}
        <div className="bg-white shadow rounded-lg p-6 bg-gradient-to-br from-green-50 to-green-100">
          <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
          <p className="mb-2 flex items-center">
            <strong>Sentiment:</strong>{" "}
            {getSentimentIconSmall(
              contact.customer_sentiment,
              "Customer Sentiment"
            )}
          </p>
          <p className="mb-2">
            <strong>Experience:</strong>{" "}
            {getLabelAndColor(contact.customer_experience_score, "experience")}
          </p>
          <p className="text-gray-500 text-sm mb-2">
            {contact.customer_experience_score_reason}
          </p>
          <p className="mb-2">
            <strong>Satisfaction:</strong>{" "}
            {getLabelAndColor(
              contact.customer_final_satisfaction,
              "satisfaction"
            )}
          </p>
          <p className="text-gray-500 text-sm">
            {contact.customer_final_satisfaction_reason}
          </p>
        </div>
      </div>

      {/* Conversation Participation Bar */}
      {agentPercent != null &&
        userPercent != null &&
        silencePercent != null && (
          <div className="mt-8 mb-4 h-5 relative rounded overflow-hidden">
            <div className="flex h-full">
              {agentPercent != null && (
                <div
                  style={{
                    width: agentPercent === 0 ? "4px" : `${agentPercent}%`,
                    backgroundColor: "#4A90E2",
                  }}
                  className="h-full relative"
                >
                  <Tippy content={`Agent: ${agentPercent.toFixed(1)}%`}>
                    <span className="absolute inset-0"></span>
                  </Tippy>
                </div>
              )}
              {userPercent != null && (
                <div
                  style={{
                    width: userPercent === 0 ? "4px" : `${userPercent}%`,
                    backgroundColor: "#50E3C2",
                  }}
                  className="h-full relative"
                >
                  <Tippy content={`Customer: ${userPercent.toFixed(1)}%`}>
                    <span className="absolute inset-0"></span>
                  </Tippy>
                </div>
              )}
              {silencePercent != null && (
                <div
                  style={{
                    width: silencePercent === 0 ? "4px" : `${silencePercent}%`,
                    backgroundColor: "#9B9B9B",
                  }}
                  className="h-full relative"
                >
                  <Tippy content={`Silence: ${silencePercent.toFixed(1)}%`}>
                    <span className="absolute inset-0"></span>
                  </Tippy>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Transcript */}
      {transcript && (
        <div
          style={{ backgroundColor: "#fbfbfb" }}
          className="bg-white shadow rounded-lg p-6 mt-8"
        >
          <h2 className="text-xl font-semibold mb-4">Transcript</h2>
          <div className="bg-gray-100 p-4 rounded-lg whitespace-pre-wrap">
            {formatTranscript(transcript)}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactDetails;
